import { Progress, type ProgressProps } from "@chakra-ui/react";
import { animate } from "framer-motion";
import React from "react";

export type AnimatedProgressProps = ProgressProps & {
  durationMs?: number;
};

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Chakra `Progress` that eases between `value` updates via Framer Motion.
 * Prefer this over `motion(Progress)` + `animate={{ value }}` — Chakra applies
 * width from the React `value` prop on an inner track, so `value` must tween.
 */
export const AnimatedProgress: React.FC<AnimatedProgressProps> = ({
  value = 0,
  durationMs = 400,
  isIndeterminate,
  ...props
}) => {
  const [displayed, setDisplayed] = React.useState(value ?? 0);
  const displayedRef = React.useRef(displayed);
  displayedRef.current = displayed;

  React.useEffect(() => {
    if (isIndeterminate) {
      return;
    }

    const target = value ?? 0;
    if (prefersReducedMotion()) {
      setDisplayed(target);
      return;
    }

    const controls = animate(displayedRef.current, target, {
      duration: durationMs / 1000,
      ease: "easeOut",
      onUpdate: (next) => {
        setDisplayed(next);
      },
    });

    return () => controls.stop();
  }, [value, durationMs, isIndeterminate]);

  return (
    <Progress
      {...props}
      isIndeterminate={isIndeterminate}
      value={isIndeterminate ? undefined : displayed}
    />
  );
};
